import { Auth } from "aws-amplify";
import Cookies from "universal-cookie";

import { getConfigs } from "app/utils/configs";
import { localDevEnabled } from "app/utils/localDev";

export interface CookieOptions {
  domain: string;
  path: string;
  secure: boolean;
}

export function getAuthCookieOptions(): CookieOptions {
  return {
    domain:
      window.location.hostname.split(".").length > 2
        ? window.location.hostname.replace(/^[^.]*/, "")
        : window.location.hostname,
    path: "/",
    secure: window.location.protocol === "https:",
  };
}

async function getSession() {
  return await Auth.currentSession().catch(() => {
    Auth.signOut();
  });
}

// Local-development backends (platform's `just local-console`, see
// app/utils/localDev) skip the Cognito sign-in but still want a real JWT so the
// cross-service validation path runs.  The local sign-in form (LocalIdpLogin)
// records which seeded user to act as; we mint that user's token straight from
// the local IdP (via the vite /api/local-token proxy in local-dev mode) and reuse it.
const LOCAL_IDP_USER_KEY = "stacklet-local-idp-user";

let localToken: { user: string; promise: Promise<string> } | null = null;

export function getLocalIdpUser(): string | null {
  return localStorage.getItem(LOCAL_IDP_USER_KEY);
}

export function setLocalIdpUser(username: string): void {
  localStorage.setItem(LOCAL_IDP_USER_KEY, username);
  localToken = null; // a different user needs a different token
}

export function clearLocalIdpUser(): void {
  localStorage.removeItem(LOCAL_IDP_USER_KEY);
  localToken = null;
  document.cookie = "stacklet-auth=; path=/; max-age=0";
  document.cookie = "stacklet-access=; path=/; max-age=0";
}

/**
 * Sets the cross-service JWT cookies used for SSO redirects out of the
 * console: Redash reads `stacklet-auth`, Superset reads `stacklet-access`.
 * Both services run on localhost so a host-only cookie reaches them
 * regardless of port; SameSite=Lax so a top-level navigation from the
 * console carries it. The local IdP mints one token per user, so both
 * cookies get the same value here — in production (see authCookieHandler)
 * they hold the ID token and access token respectively.
 */
export function setLocalIdpCookies(token: string): void {
  document.cookie = `stacklet-auth=${token}; path=/; SameSite=Lax`;
  document.cookie = `stacklet-access=${token}; path=/; SameSite=Lax`;
}

async function getLocalToken(): Promise<string> {
  const user = getLocalIdpUser();
  if (!user) {
    // The sign-in gate renders children only once a user is chosen, so this
    // is unreachable in practice; fail soft rather than throw mid-request.
    return "Unauthenticated";
  }
  if (!localToken || localToken.user !== user) {
    const { deployment } = await getConfigs();
    const tokenUrl = deployment.local_idp_token_url;
    if (!tokenUrl) {
      throw new Error("local IdP token URL missing from deployment config");
    }
    const promise = fetch(`${tokenUrl}?username=${encodeURIComponent(user)}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`local IdP token request failed: ${res.status}`);
        }
        return res.json();
      })
      .then((body) => {
        const token = body.access_token as string;
        setLocalIdpCookies(token);
        return token;
      })
      .catch((err) => {
        // Don't cache a failure -- let the next request retry.
        localToken = null;
        throw err;
      });
    localToken = { user, promise };
  }
  return localToken.promise;
}

export async function getAuthToken(): Promise<string> {
  if (localDevEnabled()) {
    return getLocalToken();
  }

  const session = await getSession();

  if (session) {
    return session.getAccessToken().getJwtToken();
  }

  return "Unauthenticated";
}

export async function getIdToken(): Promise<string> {
  const session = await getSession();

  if (session) {
    return session.getIdToken().getJwtToken();
  }

  return "Unauthenticated";
}

export async function authCookieHandler() {
  const cookies = new Cookies();
  for (const key in cookies.getAll()) {
    if (
      key.startsWith("CognitoIdentityServiceProvider.") &&
      key.endsWith(".userData") &&
      Object.keys(cookies.get(key)).length === 0
    ) {
      // Work around https://github.com/aws-amplify/amplify-js/issues/11106
      // For some reason, if the userData cookie is empty, the Auth.currentSession
      // below explodes and the .catch() on it doesn't help. Removing the cookie
      // just forces Amplify to rebuild it.
      cookies.remove(key, getAuthCookieOptions());
    }
  }
  // can't use getSession because that will cause a signOut event infinite loop
  const session = await Auth.currentSession().catch(() => null);

  if (session) {
    cookies.set("stacklet-auth", await getIdToken(), getAuthCookieOptions());
    cookies.set(
      "stacklet-access",
      await getAuthToken(),
      getAuthCookieOptions(),
    );
  } else {
    cookies.remove("stacklet-auth", getAuthCookieOptions());
    cookies.remove("stacklet-access", getAuthCookieOptions());
  }
}
